import React, { ReactElement, memo, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormControl } from '@dexma/ui-components';
import i18n from 'asset/translation';
import FormSubmitModal from 'adapter/in/common/components/FormSubmitModal/FormSubmitModal';

export const NAME_MAX_LENGTH = 140;

export type CreateNameFormData = {
    name: string;
};

export type CreateModalProps = {
    error: boolean;
    success: boolean;
    isLoading: boolean;
    showModal: boolean;
    handleCloseModal: () => void;
    handleOnSubmit: (data: CreateNameFormData) => void;
    defaultValue?: string;
    title: string;
    dataIdContext: {
        tryAgain: string;
        inputName: string;
        save: string;
        cancel: string;
    };
    message: string;
    placeHolder?: string;
    proceedButtonText?: string;
};

const SingleNameInputModal = memo(
    (props: CreateModalProps): ReactElement => {
        const {
            showModal,
            handleCloseModal,
            error,
            success,
            isLoading,
            handleOnSubmit: handleExternalSubmit,
            defaultValue,
            title,
            message,
            placeHolder,
            proceedButtonText,
            dataIdContext,
        } = props;

        const { handleSubmit, reset: formReset, control, setValue } = useForm<CreateNameFormData>();

        useEffect(() => {
            if (showModal && !defaultValue) {
                formReset();
            }
            if (showModal && defaultValue) {
                setValue('name', defaultValue);
            }
        }, [showModal, defaultValue]);

        const handleOnSubmit = () => {
            handleSubmit(handleExternalSubmit)();
        };

        return (
            <FormSubmitModal
                isLoading={isLoading}
                showModal={showModal}
                handleCloseModal={handleCloseModal}
                title={title}
                error={error}
                success={success}
                dataIdContext={dataIdContext}
                message={message}
                handleOnSubmit={handleOnSubmit}
                proceedButtonText={proceedButtonText}
            >
                <Controller
                    control={control}
                    rules={{
                        required: { value: true, message: i18n.t('dashboards.create.noNameProvided') },
                        maxLength: { value: NAME_MAX_LENGTH, message: i18n.t('dashboards.create.nameTooLong') },
                    }}
                    defaultValue={defaultValue}
                    name='name'
                    render={({ field: { onChange, onBlur, value }, fieldState: { error: fieldError } }) => (
                        <FormControl
                            data-testid='input-name'
                            onChange={onChange}
                            onBlur={onBlur}
                            control='Input'
                            disabled={isLoading || success}
                            value={value ?? ''}
                            placeholder={placeHolder}
                            message={fieldError?.message}
                            error={!!fieldError}
                            dataId={props.dataIdContext.inputName}
                        />
                    )}
                />
            </FormSubmitModal>
        );
    }
);

export default SingleNameInputModal;
